import React, { useState, useEffect } from 'react';
import { getApiUrl } from '../../config/api';
import { ensureUserDirectoryLoaded, lookupProfilePicture, subscribeUserDirectory } from '../../utils/userDirectory';

interface AvatarProps {
  name?: string;
  userId?: string;
  /** Pass this if the caller already has the picture URL (e.g. the current logged-in user) to skip the directory lookup. */
  profilePicture?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const SIZE_CLASSES: Record<string, string> = {
  xs: 'w-5 h-5 text-[9px]',
  sm: 'w-7 h-7 text-xs',
  md: 'w-9 h-9 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-24 h-24 text-3xl',
};

const COLORS = ['bg-blue-100 text-blue-700', 'bg-emerald-100 text-emerald-700', 'bg-violet-100 text-violet-700', 'bg-amber-100 text-amber-700', 'bg-rose-100 text-rose-700', 'bg-cyan-100 text-cyan-700'];

const colorFor = (key: string) => {
  const sum = key.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return COLORS[sum % COLORS.length];
};

const initialsFor = (name?: string) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '');
};

// Shows a user's uploaded profile picture wherever their name appears, falling
// back to a colored initials circle when they haven't uploaded one — used
// consistently across cards, lists, timelines, comments, etc. instead of
// showing a bare name with no visual identity.
const Avatar: React.FC<AvatarProps> = ({ name, userId, profilePicture, size = 'sm', className = '' }) => {
  const [resolvedPicture, setResolvedPicture] = useState(profilePicture || lookupProfilePicture(userId || name));

  useEffect(() => {
    if (profilePicture) {
      setResolvedPicture(profilePicture);
      return;
    }
    const key = userId || name;
    if (!key) {
      setResolvedPicture('');
      return;
    }

    let cancelled = false;
    const applyLookup = () => {
      if (cancelled) return;
      // Unconditional — a falsy/empty result (switching to someone who
      // hasn't uploaded a photo) must clear whatever was showing before.
      // This used to only ever ADD a picture, never REMOVE one, so an
      // Avatar instance reused for a different person without remounting
      // (e.g. the chat header's single avatar slot, swapped between
      // conversations rather than recreated) kept showing the PREVIOUS
      // person's photo indefinitely once one had ever loaded.
      setResolvedPicture(lookupProfilePicture(key));
    };

    applyLookup();
    ensureUserDirectoryLoaded().then(applyLookup);
    const unsubscribe = subscribeUserDirectory(applyLookup);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [userId, name, profilePicture]);

  const sizeClass = SIZE_CLASSES[size];

  if (resolvedPicture) {
    return (
      <img
        src={getApiUrl(resolvedPicture.replace(/^\//, ''))}
        alt=""
        className={`${sizeClass} rounded-full object-cover flex-shrink-0 ${className}`}
      />
    );
  }

  return (
    <div className={`${sizeClass} rounded-full flex items-center justify-center font-semibold flex-shrink-0 uppercase ${colorFor(userId || name || '?')} ${className}`}>
      {initialsFor(name)}
    </div>
  );
};

export default Avatar;
